import { useState, useCallback, useRef } from 'react';
import { FaIcon } from '@ifixit/ui/components/icon';
import { SentryError } from '@ifixit/sentry';
import { RegisterCode } from './RegisterCode';
import { Email } from './Email';
import { LoginCode } from './LoginCode';
import { Auth } from 'Shared/auth';
import { Recaptcha } from 'Shared/recaptcha';
import type { FlowStep, AuthInstance, AuthResponse, WindowApp } from './types';
import initializeComponent from 'Shared/react-initialize';
import { useTranslations } from '@ifixit/i18n';
import type { TranslationFunction } from '@ifixit/i18n/types';

interface JoinPageProps {
   privacyPolicyUrl: string;
}

export const JoinPage = ({ privacyPolicyUrl }: JoinPageProps) => {
   const t = useTranslations('Login');
   const [flowStep, setFlowStep] = useState<FlowStep>('email');
   const [userEmail, setUserEmail] = useState<string>('');
   const recaptchaRef = useRef<HTMLDivElement>(null);

   const handleNavigateToLogin = useCallback(async (email: string) => {
      try {
         const app = (window as any).App as WindowApp;
         let captchaResult: string | undefined;
         if (app?.recaptchaSiteKey) {
            captchaResult = await Recaptcha.executeInvisible(
               recaptchaRef.current,
               app.recaptchaSiteKey,
               'login'
            );
         }

         const auth = Auth as unknown as AuthInstance;
         await new Promise<void>((resolve, reject) => {
            void auth.sendGetLoginCode(
               { login: email, captchaResult },
               (response: AuthResponse) => {
                  if (response.loggedIn === false && response.reason) {
                     reject(new SentryError(`Login code request failed: ${response.reason}`));
                  } else {
                     resolve();
                  }
               }
            );
         });
         setUserEmail(email);
         setFlowStep('login');
      } catch (error) {
         console.error('Failed to send login code:', error);
      }
   }, []);

   const handleNavigateToRegister = useCallback(async (email: string) => {
      try {
         const app = (window as any).App as WindowApp;
         let captchaResult: string | undefined;
         if (app?.recaptchaSiteKey) {
            captchaResult = await Recaptcha.executeInvisible(
               recaptchaRef.current,
               app.recaptchaSiteKey,
               'register'
            );
         }

         const auth = Auth as unknown as AuthInstance;
         await new Promise<void>((resolve, reject) => {
            void auth.sendGetRegisterCode(
               { login: email, captchaResult },
               (response: AuthResponse) => {
                  if (response.loggedIn === false && response.reason) {
                     reject(new SentryError(`Register code request failed: ${response.reason}`));
                  } else {
                     resolve();
                  }
               }
            );
         });
         setUserEmail(email);
         setFlowStep('register');
      } catch (error) {
         console.error('Failed to send register code:', error);
      }
   }, []);

   const handleBackToEmail = useCallback(() => {
      setFlowStep('email');
   }, []);

   return (
      <>
         <style>{`:where(body) { background-color: var(--color-gray-50); }`}</style>
         <div
            className="md:my-14 md:gap-8 gap-4 flex flex-col md:flex-row items-center justify-center"
            data-testid="loginContainer"
         >
            <JoinLeftContent t={t} />
            <div className="max-sm:w-full">
               <div className="text-center text-base mt-6 mb-4 w-full">
                  <span>{t('alreadyHaveAnAccount')}</span>
                  &nbsp;
                  <a href="/LogIn" className="text-brand-500 font-medium">
                     {t('logIn')}
                  </a>
                  <span>.</span>
               </div>
               <div className="w-full lg:w-auto flex flex-col">
                  {flowStep === 'email' && (
                     <Email
                        onNavigateToLogin={handleNavigateToLogin}
                        onNavigateToRegister={handleNavigateToRegister}
                     />
                  )}
                  {flowStep === 'login' && (
                     <LoginCode email={userEmail} onNavigateBack={handleBackToEmail} />
                  )}
                  {flowStep === 'register' && (
                     <RegisterCode email={userEmail} privacyPolicyUrl={privacyPolicyUrl} />
                  )}
               </div>
            </div>
         </div>
         <div ref={recaptchaRef} className="hidden" />
         {(window as any).App?.dataProtectionMessage && (
            <p
               dangerouslySetInnerHTML={{
                  __html: (window as any).App.dataProtectionMessage,
               }}
            />
         )}
      </>
   );
};

const JoinLeftContent = ({ t }: { t: TranslationFunction }) => (
   <div className="font-base md:w-[416px]">
      <h2 className="text-2xl md:text-3xl font-medium max-sm:text-center">
         {t('joinTheCommunity')}
      </h2>
      <div className="mt-6 space-y-4 flex flex-col text-base">
         <div className="flex items-center">
            <FaIcon
               name="screwdriver-wrench"
               family="duotone"
               className="text-[24px] text-brand-500 my-auto mr-2"
            />
            {t('learnFromFixers')}
         </div>
         <div className="flex items-center">
            <FaIcon
               name="pen-to-square"
               family="duotone"
               className="text-[24px] text-brand-500 my-auto mr-2"
            />
            {t('shareYourExpertise')}
         </div>
         <div className="flex items-center">
            <FaIcon
               name="certificate"
               family="duotone"
               className="text-[24px] text-brand-500 my-auto mr-2"
            />
            {t('participateToEarnReputation')}
         </div>
      </div>
   </div>
);

initializeComponent('JoinPage', JoinPage);
